import { bech32, bech32m, createBase58check } from '@scure/base';
import { sha256 } from '@noble/hashes/sha2.js';
import { concat, hash160 } from './bytes';

/** Network parameters. Mainnet values are COINWOW's live, immutable parameters. */
export interface Network {
  name: string;
  bech32HRP: string;
  pubkeyHashPrefix: number;
  scriptHashPrefix: number;
  /** BIP32 versions (xprv / xpub). */
  bip32: { private: number; public: number };
  /** BIP44 coin type in m/84'/coin'/0'. COINWOW Core uses 0 on mainnet. */
  coinType: number;
}

export const MAINNET: Network = {
  name: 'main',
  bech32HRP: 'cw',
  pubkeyHashPrefix: 28,
  scriptHashPrefix: 88,
  bip32: { private: 0x0488ade4, public: 0x0488b21e },
  coinType: 0,
};

export const REGTEST: Network = {
  name: 'regtest',
  bech32HRP: 'bcrt',
  pubkeyHashPrefix: 111,
  scriptHashPrefix: 196,
  bip32: { private: 0x04358394, public: 0x043587cf },
  coinType: 1,
};

const base58check = createBase58check(sha256);

export type AddressKind = 'p2pkh' | 'p2sh' | 'p2wpkh' | 'p2wsh' | 'p2tr' | 'witness_unknown';

export class AddressError extends Error {
  constructor() {
    super('invalid address');
  }
}

export interface Address {
  /** Canonical string (bech32 in lowercase). */
  string: string;
  scriptPubKey: Uint8Array;
  kind: AddressKind;
}

/**
 * Parses an address typed, pasted or scanned by the user. Accepts an optional
 * "coinwow:" URI prefix (query parameters ignored).
 */
export function parseAddress(input: string, network: Network = MAINNET): Address {
  let s = input.trim();
  if (s.toLowerCase().startsWith('coinwow:')) {
    s = s.slice('coinwow:'.length);
    const q = s.indexOf('?');
    if (q >= 0) s = s.slice(0, q);
  }
  if (s.toLowerCase().startsWith(network.bech32HRP + '1')) {
    // Mixed case is invalid in bech32.
    if (s !== s.toLowerCase() && s !== s.toUpperCase()) throw new AddressError();
    const lower = s.toLowerCase();
    let version: number, program: Uint8Array;
    try {
      const d = bech32.decode(lower as `${string}1${string}`, 90);
      version = d.words[0];
      if (version !== 0) throw new AddressError();
      program = bech32.fromWords(d.words.slice(1));
      if (d.prefix !== network.bech32HRP) throw new AddressError();
    } catch (e) {
      try {
        const d = bech32m.decode(lower as `${string}1${string}`, 90);
        version = d.words[0];
        if (version === 0 || version > 16) throw new AddressError();
        program = bech32m.fromWords(d.words.slice(1));
        if (d.prefix !== network.bech32HRP) throw new AddressError();
      } catch {
        throw new AddressError();
      }
    }
    if (program.length < 2 || program.length > 40) throw new AddressError();
    if (version === 0 && program.length !== 20 && program.length !== 32) throw new AddressError();
    const script = concat(Uint8Array.of(version === 0 ? 0 : 0x50 + version, program.length), program);
    let kind: AddressKind = 'witness_unknown';
    if (version === 0 && program.length === 20) kind = 'p2wpkh';
    else if (version === 0 && program.length === 32) kind = 'p2wsh';
    else if (version === 1 && program.length === 32) kind = 'p2tr';
    return { string: lower, scriptPubKey: script, kind };
  }
  let payload: Uint8Array;
  try {
    payload = base58check.decode(s);
  } catch {
    throw new AddressError();
  }
  if (payload.length !== 21) throw new AddressError();
  const h = payload.slice(1);
  if (payload[0] === network.pubkeyHashPrefix) {
    return { string: s, scriptPubKey: concat(Uint8Array.of(0x76, 0xa9, 0x14), h, Uint8Array.of(0x88, 0xac)), kind: 'p2pkh' };
  }
  if (payload[0] === network.scriptHashPrefix) {
    return { string: s, scriptPubKey: concat(Uint8Array.of(0xa9, 0x14), h, Uint8Array.of(0x87)), kind: 'p2sh' };
  }
  throw new AddressError();
}

/** Native segwit (P2WPKH) address of a compressed public key: "cw1q…". */
export function p2wpkh(publicKey: Uint8Array, network: Network = MAINNET): Address {
  const program = hash160(publicKey);
  const s = bech32.encode(network.bech32HRP, [0, ...bech32.toWords(program)]);
  return { string: s, scriptPubKey: concat(Uint8Array.of(0x00, 0x14), program), kind: 'p2wpkh' };
}

/** Core's dust threshold for an output at the default 3 sat/vB dust relay fee. */
export function dustThreshold(script: Uint8Array): number {
  const outputSize = 8 + 1 + script.length;
  const isWitness =
    script.length >= 4 &&
    script.length <= 42 &&
    (script[0] === 0 || (script[0] >= 0x51 && script[0] <= 0x60)) &&
    script[1] + 2 === script.length;
  const spendSize = isWitness ? 32 + 4 + 1 + Math.floor(107 / 4) + 4 : 32 + 4 + 1 + 107 + 4;
  return Math.floor(((outputSize + spendSize) * 3000) / 1000);
}
